import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {Modal, Icon} from '@/components/atoms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {COLOR_CODE} from '@/utils/constants/colors';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {downloadDigitalCodeReport} from '@/services/rewardCatalog';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {useAppDispatch} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {convertToIsoString} from '@/utils/commons';
import {logger} from '@/helpers/logger';

const DateInput = React.forwardRef(
  ({value, onClick, placeholder, isError}, ref) => (
    <button
      type='button'
      onClick={onClick}
      ref={ref}
      className={`datepicker-input-btn flex w-full items-center justify-between rounded border text-left ${isError ? 'datepicker-input-btn--error' : ''}`}>
      <span
        className={`datepicker-input-btn__text ${value ? 'datepicker-input-btn__text--filled' : 'datepicker-input-btn__text--placeholder'}`}>
        {value || placeholder || 'Choose date'}
      </span>
      <span className='datepicker-input-btn__icon'>
        <Icon icon='calendar_today' size='small' />
      </span>
    </button>
  )
);

DateInput.displayName = 'DateInput';

DateInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  placeholder: PropTypes.string,
  isError: PropTypes.bool
};

const DownloadDigitalCodeReportModal = ({showModal, onClose}) => {
  const dispatch = useAppDispatch();
  const {downloadFromBlobUrl} = useAzureBlob();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setStartDateError('');
    setEndDateError('');
    if (endDate && date > endDate) {
      setStartDateError('Start date cannot be after end date');
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setEndDateError('');
    setStartDateError('');
    if (startDate && date && startDate > date) {
      setEndDateError('End date cannot be before start date');
    }
  };

  const isDownloadDisabled =
    !startDate ||
    !endDate ||
    !!startDateError ||
    !!endDateError ||
    isDownloading;

  const resetState = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setStartDateError('');
    setEndDateError('');
    setShowCancelConfirm(false);
    setIsDownloading(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await downloadDigitalCodeReport({
        startDate: convertToIsoString(startDate, true, false),
        endDate: convertToIsoString(endDate, false, true)
      });

      if (response?.success && response?.blobUrl && response?.sasToken) {
        const csvText = await downloadFromBlobUrl(
          response.blobUrl,
          response.sasToken
        );
        if (!csvText) throw new Error('Blob download failed');
        const fileName = `DigitalCodeReport_${getDateWithTimeString()}`;
        downloadCSVFiles(csvText, fileName);
        handleCloseModal();
      } else {
        handleCloseModal();
        setTimeout(() => {
          dispatch(
            setToastNotifications({
              show: true,
              message:
                response?.message ||
                'No digital codes found for the specified date range',
              type: 'error'
            })
          );
        }, 300);
      }
    } catch (error) {
      logger.error('Error downloading digital code report:', error);
      handleCloseModal();
      setTimeout(() => {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Failed to download digital code report',
            type: 'error'
          })
        );
      }, 300);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCancelClick = () => {
    if (startDate || endDate) {
      setShowCancelConfirm(true);
    } else {
      handleCloseModal();
    }
  };

  return (
    <>
      <Modal
        type={MODAL_TYPE.FLUID}
        showModal={showModal && !showCancelConfirm}
        isDark
        title='Download Digital Code Report'
        primaryButtonText={isDownloading ? 'Downloading...' : 'Download'}
        secondaryButtonText='Cancel'
        showSecondaryButton
        showPrimaryButton
        primaryButtonDisabled={isDownloadDisabled}
        onCloseModal={handleCancelClick}
        onClickPrimaryButton={handleDownload}
        onClickSecondaryButton={handleCancelClick}
        primaryButtonProps={{
          iconLeading: 'file_download'
        }}
        content={
          <div
            className='flex flex-col gap-4 pt-2'
            style={{overflow: 'visible', minHeight: '320px'}}>
            <p className='text-sm text-gray-300'>
              Select a start and end date to generate and download the report.
              <br />
              The report will include all digital code activity within the
              selected date range.
            </p>
            <div className='grid grid-cols-2 gap-6'>
              <div className='flex flex-col gap-1'>
                <label
                  htmlFor='start-date'
                  className='text-sm font-medium'
                  style={{color: COLOR_CODE._D0D0D0}}>
                  Start Date*
                </label>
                <DatePicker
                  id='start-date'
                  selected={startDate}
                  onChange={handleStartDateChange}
                  dateFormat='yyyy-MM-dd'
                  placeholderText='yyyy-mm-dd'
                  customInput={<DateInput isError={!!startDateError} />}
                  popperClassName='z-[9999]'
                  popperPlacement='bottom-start'
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  dateFormatCalendar='MMMM'
                  dropdownMode='select'
                  yearDropdownItemNumber={15}
                  scrollableYearDropdown
                />
                {startDateError && (
                  <span className='mt-1 flex items-center gap-1 text-xs text-red-400'>
                    <Icon icon='error' size='small' />
                    {startDateError}
                  </span>
                )}
              </div>
              <div className='flex flex-col gap-1'>
                <label
                  htmlFor='end-date'
                  className='text-sm font-medium'
                  style={{color: COLOR_CODE._D0D0D0}}>
                  End Date*
                </label>
                <DatePicker
                  id='end-date'
                  selected={endDate}
                  onChange={handleEndDateChange}
                  dateFormat='yyyy-MM-dd'
                  placeholderText='yyyy-mm-dd'
                  customInput={<DateInput isError={!!endDateError} />}
                  popperClassName='z-[9999]'
                  popperPlacement='bottom-start'
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  dateFormatCalendar='MMMM'
                  dropdownMode='select'
                  yearDropdownItemNumber={15}
                  scrollableYearDropdown
                />
                {endDateError && (
                  <span className='mt-1 flex items-center gap-1 text-xs text-red-400'>
                    <Icon icon='error' size='small' />
                    {endDateError}
                  </span>
                )}
              </div>
            </div>
          </div>
        }
      />

      {showCancelConfirm && (
        <Modal
          type={MODAL_TYPE.SMALL}
          showModal={showCancelConfirm}
          isDark
          title='Are you sure you want to cancel?'
          showPrimaryButton
          primaryButtonText='Yes'
          onClickPrimaryButton={handleCloseModal}
          showSecondaryButton
          secondaryButtonText='No'
          onClickSecondaryButton={() => setShowCancelConfirm(false)}
          onCloseModal={() => setShowCancelConfirm(false)}
          footerProps={{
            size: 'medium',
            type: 'primary'
          }}
        />
      )}
    </>
  );
};

DownloadDigitalCodeReportModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default DownloadDigitalCodeReportModal;
